import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Package, Truck, Headphones, MapPin, Clock, MessageCircle, AlertCircle, Factory, Printer } from 'lucide-react';
import { Product } from '../types';
import apiService from '../services/api';
import SEO from '../components/SEO';
import { companyConfig, getCompanyTagline } from '../config/company';
import { toProductImageUrl } from '../utils/imageUrl';

// ✅ Movidos fuera del componente: son estáticos, no hace falta recrearlos en cada render
const advantages = [
  {
    icon: <Package className="h-8 w-8 text-primary-600" />,
    title: "Calidad Garantizada",
    description: `Material LDPE de primera calidad desde el año ${companyConfig.foundationYear}`
  },
  {
    icon: <Truck className="h-8 w-8 text-primary-600" />,
    title: "Entrega Rápida",
    description: "Pedidos estándar en 1 día\nA medida en 7 días"
  },
  {
    icon: <Headphones className="h-8 w-8 text-primary-600" />,
    title: "Asesoramiento Técnico",
    description: "Recomendaciones personalizadas para cada necesidad"
  }
];

const services = [
  {
    id: "fabricacion",
    icon: <Factory className="h-6 w-6 text-white" />,
    title: "Fabricación Personalizada",
    description: "Fabricamos bolsas LDPE a medida para comercio e industria, ajustando ancho, largo, color y espesor según tu necesidad.",
    image: "/images/fabricacion-personalizada.png",
    imagePosition: "object-center",
    highlights: [
      "Medidas especiales hasta 1.20 m de ancho",
      "Espesores hasta 200 micrones",
      "Producción estable y control de calidad continuo",
      "Desde pedidos iniciales hasta volúmenes mayoristas"
    ]
  },
  {
    id: "entrega",
    icon: <Truck className="h-6 w-6 text-white" />,
    title: "Servicio de Entrega",
    description: "Coordinamos la logística para que recibas tu pedido en tiempo y forma, con seguimiento claro durante el proceso.",
    image: "/images/servicio-entrega.png",
    imagePosition: "object-[center_35%]",
    highlights: [
      "Cobertura en San Juan y zonas cercanas",
      "Ventanas de entrega planificadas",
      "Comunicación directa por WhatsApp"
    ]
  },
  {
    id: "impresion",
    icon: <Printer className="h-6 w-6 text-white" />,
    title: "Impresión Terciarizada",
    description: "Te ayudamos a personalizar tus bolsas con impresión de logo y textos para reforzar la identidad de tu marca.",
    image: "/images/impresion-tercerizada.png",
    imagePosition: "object-center",
    highlights: [
      "Asesoramiento para adaptar artes y colores",
      "Opciones para series comerciales y mayoristas",
      "Terminación prolija y legible en cada lote"
    ]
  }
];

// ✅ Función helper para parsear horarios sin usar dangerouslySetInnerHTML
const parseHours = (hoursString: string): { days: string; hours: string } => {
  const parts = hoursString.split(' de ');
  if (parts.length === 2) {
    return { days: parts[0], hours: parts[1] };
  }
  return { days: hoursString, hours: '' };
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // ✅ Estado de error agregado
  const [fetchError, setFetchError] = useState<string | null>(null);
  const foundationYear = Number(companyConfig.foundationYear);

  // Atajo para el personal: F10 en la pagina principal va directo al login del panel admin.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F10') {
        e.preventDefault();
        navigate('/login');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        // ✅ limit: 3 en la request para no traer datos de más
        const response = await apiService.getProducts({ featured: true, limit: 3 });
        setFeaturedProducts(response.products);
      } catch (error) {
        console.error('Error fetching featured products:', error);
        // ✅ Guardamos el error para mostrar feedback al usuario
        setFetchError('No pudimos cargar los productos. Por favor, intentá de nuevo más tarde.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  const parsedHours = parseHours(companyConfig.hours);

  // ✅ URL de WhatsApp generada desde la config
  const whatsappUrl = `https://wa.me/${companyConfig.whatsapp.replace(/\D/g, '')}?text=Hola%2C%20me%20gustar%C3%ADa%20solicitar%20una%20cotizaci%C3%B3n`;

  return (
    <div className="min-h-screen">
      <SEO
        title="Inicio"
        description="Plásticos Polcen - Fabricamos bolsas de polietileno de baja densidad (LDPE) de alta calidad. Especialistas en bolsas comerciales, industriales y domésticas. Entrega rápida en San Juan, Argentina."
        keywords="bolsas plásticas, polietileno, LDPE, bolsas comerciales, bolsas industriales, embalaje, San Juan, Argentina, plásticos, fabricación"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            {/* ✅ Badge de prueba social con años de experiencia */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-primary-100 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
              <CheckCircle className="h-4 w-4" />
              Desde {companyConfig.foundationYear} — más de {new Date().getFullYear() - foundationYear} años en el mercado
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Plásticos Polcen
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              {getCompanyTagline()}
            </p>
            <p className="text-lg mb-10 text-primary-200 max-w-3xl mx-auto">
              Especialistas en bolsas de plástico LDPE personalizadas.
              Todos los anchos, largos y colores. Calidad garantizada con entrega rápida.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/productos"
                className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
              >
                Ver Productos
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              {/* ✅ CTA de cotización apunta a WhatsApp directamente */}
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors inline-flex items-center justify-center gap-2"
              >
                <MessageCircle className="h-5 w-5" />
                Solicitar Cotización por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Ventajas Competitivas */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              ¿Por qué elegir Plásticos Polcen?
            </h2>
            <p className="text-lg text-gray-600">
              Nuestras ventajas competitivas nos distinguen en el mercado
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* ✅ key estable usando el título en vez de index */}
            {advantages.map((advantage) => (
              <div key={advantage.title} className="text-center p-6 bg-white rounded-lg shadow-md">
                <div className="flex justify-center mb-4">
                  {advantage.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {advantage.title}
                </h3>
                <p className="text-gray-600 whitespace-pre-line">
                  {advantage.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Productos Destacados */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Productos Destacados
            </h2>
            <p className="text-lg text-gray-600">
              Conoce nuestras categorías principales de bolsas de plástico
            </p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gray-200 animate-pulse rounded-lg h-64"></div>
              ))}
            </div>
          ) : fetchError ? (
            // ✅ Estado de error con mensaje claro para el usuario
            <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
              <AlertCircle className="h-10 w-10 text-gray-400" />
              <p className="text-gray-500">{fetchError}</p>
              <Link to="/productos" className="text-primary-600 font-medium hover:underline">
                Ver catálogo completo →
              </Link>
            </div>
          ) : featuredProducts.length === 0 ? (
            // ✅ Estado vacío explícito
            <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
              <Package className="h-10 w-10 text-gray-400" />
              <p className="text-gray-500">No hay productos destacados por el momento.</p>
              <Link to="/productos" className="text-primary-600 font-medium hover:underline">
                Ver catálogo completo →
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* ✅ key usando product.id (estable) */}
              {featuredProducts.map((product) => (
                <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  {/* ✅ Muestra imagen real si existe, fallback al ícono */}
                  <div className="h-64 bg-white flex items-center justify-center overflow-hidden">
                    {product.image ? (
                      <img
                        src={toProductImageUrl(product.image)}
                        alt={product.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Package className="h-16 w-16 text-gray-400" />
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {product.name}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {product.description}
                    </p>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 capitalize">
                        {product.category}
                      </span>
                    </div>
                    {/* ✅ CTA por producto */}
                    <Link
                      to={`/contacto?asunto=cotizacion&producto=${encodeURIComponent(product.name)}`}
                      className="mt-4 w-full border border-primary-600 text-primary-600 py-2 rounded-lg text-sm font-medium hover:bg-primary-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Cotizar
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/productos"
              className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors inline-flex items-center"
            >
              Ver Todos los Productos
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Servicios */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nuestros Servicios
            </h2>
            <p className="text-lg text-gray-600">
              Soluciones completas para todas tus necesidades en bolsas de plástico
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* ✅ key estable usando id del servicio */}
            {services.map((service) => (
              <div key={service.id} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                <div className="relative h-44">
                  <img
                    src={service.image}
                    alt={service.title}
                    className={`w-full h-full object-cover ${service.imagePosition}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent" />
                  <div className="absolute left-4 bottom-4 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary-600 shadow-lg">
                    {service.icon}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.highlights.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-primary-600 mt-0.5 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ Sección de contacto simplificada — CTA directo en vez de repetir info del footer */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            ¿Necesitás una cotización?
          </h2>
          <p className="text-xl text-primary-100 mb-4">
            Respondemos por WhatsApp en minutos
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-primary-200 text-sm mb-10">
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {parsedHours.days}
            </span>
            <span className="hidden sm:block">·</span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {parsedHours.hours}
            </span>
            <span className="hidden sm:block">·</span>
            <span className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              San Juan, Argentina
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2"
            >
              <MessageCircle className="h-5 w-5" />
              Escribinos por WhatsApp
            </a>
            <Link
              to="/contacto"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors inline-flex items-center justify-center"
            >
              Ver formulario de contacto
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
